import { selectShu2SearchCondition } from '../../utils/shu';
import { ShuOption } from '../shu';
import { KiSyokenResolvedSearchParams, KiSyokenSearchParams } from './types';

/**
 * 種別（shuGroupIds / shuList）を解決した検索条件の一部
 */
export type ResolvedKiSyokenShuCondition = ReturnType<
  typeof selectShu2SearchCondition
>;

/**
 * 種別条件を解決する。
 *
 * WHAT: `rawSearchParams` に種別があればそれを優先し、無ければ `currentShu` から解決する。
 * WHY: 種別セレクター経由（params に種別あり）と、UI 選択状態（currentShu）の
 * どちらから来ても、同じ解決ロジックで種別を確定させる（claris-general#6200）。
 */
export const resolveKiSyokenShuCondition = (
  rawSearchParams: Pick<KiSyokenSearchParams, 'shuGroupIds' | 'shuList'>,
  currentShu: ShuOption
): ResolvedKiSyokenShuCondition => {
  const rawShu = rawSearchParams.shuGroupIds || rawSearchParams.shuList;
  return rawShu
    ? {
        shuGroupIds: rawSearchParams.shuGroupIds,
        shuList: rawSearchParams.shuList,
      }
    : selectShu2SearchCondition(currentShu);
};

/**
 * 解決済みの種別条件をマージして、種別解決済み名義のパラメータを返す。
 *
 * WHAT: `baseSearchParams` に `resolvedShuCondition` を必ず含めた完成形を返す。
 * 戻り値は `as` によるコンパイル時の型付けであり、ランタイムに印は付かない。
 *
 * WHY: メイン検索とタグ平均など複数経路へ、種別解決済みの条件を単一ソースから渡す
 * （claris-general#6200）。未解決 params の素の代入は後続 action の引数型で防ぐ。
 * `as KiSyokenResolvedSearchParams` の直接使用は避け、この関数か
 * `asKiSyokenResolvedSearchParams` に寄せる。
 */
export const buildKiSyokenResolvedSearchParams = (
  baseSearchParams: KiSyokenSearchParams,
  resolvedShuCondition: ResolvedKiSyokenShuCondition
): KiSyokenResolvedSearchParams =>
  ({
    ...baseSearchParams,
    ...resolvedShuCondition,
  } as KiSyokenResolvedSearchParams);

/**
 * 既に種別が含まれている検索条件を、解決済み名義として型付けする（escape hatch）。
 *
 * WHAT: オブジェクトを変更せず、コンパイル時の名義だけ `KiSyokenResolvedSearchParams` にする。
 * 種別が本当に解決済みかは検証しない。
 *
 * WHY: お気に入り適用のように `searchParams` に保存時点の種別が既に含まれる経路向け。
 * 種別を解決し直す必要がある場合は `buildKiSyokenResolvedSearchParams` を使うこと。
 */
export const asKiSyokenResolvedSearchParams = (
  searchParamsWithResolvedShu: KiSyokenSearchParams
): KiSyokenResolvedSearchParams =>
  searchParamsWithResolvedShu as KiSyokenResolvedSearchParams;
